import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";

const KINDS = [["clientes", "Clientes e veículos"], ["produtos", "Produtos do estoque"], ["servicos", "Serviços (mão de obra)"]];
const ST = { novo: ["Novo", "text-green-400"], atualizar: ["Atualizar", "text-sky-300"], ignorado: ["Ignorado", "text-zinc-400"], erro: ["Erro", "text-red-400"] };

function Report({ rep, final }) {
  return (
    <div data-testid={final ? "import-report" : "import-preview"} className="rounded-xl border border-zinc-800 p-3 space-y-2">
      <div className="flex flex-wrap gap-4 text-sm">
        <span className="text-green-400 font-bold">{final ? "Importados" : "Novos"}: {rep.imported}</span>
        <span className="text-sky-300 font-bold">{final ? "Atualizados" : "A atualizar"}: {rep.updated}</span>
        <span className="text-zinc-400 font-bold">Ignorados/erros: {rep.ignored.length}</span>
      </div>
      <div className="max-h-72 overflow-y-auto text-xs divide-y divide-zinc-800">
        {(final ? rep.ignored.map((x) => ({ ...x, status: "ignorado" })) : rep.preview).map((p) => (
          <div key={p.line} className="flex justify-between gap-2 py-1">
            <span className="text-zinc-300 truncate">Linha {p.line}{p.label ? ` · ${p.label}` : ""}</span>
            <span className={ST[p.status]?.[1]}>{ST[p.status]?.[0]}{p.reason ? ` — ${p.reason}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OwnerImport() {
  const [kind, setKind] = useState("clientes");
  const [fields, setFields] = useState(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("skip");
  const [preview, setPreview] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/import/fields").then(({ data }) => setFields(data)).catch(() => {}); }, []);
  const reset = () => { setParsed(null); setPreview(null); setReport(null); };
  const upload = async () => {
    if (!file) { return; }
    setBusy(true); reset();
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data); setMapping(data.mapping);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/run", { kind, rows: parsed.rows, mapping, mode, dry_run: dry });
      if (dry) { setPreview(data); } else { setReport(data); setPreview(null); toast.success("Importação concluída"); }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const template = async () => {
    try {
      const { data } = await api.get(`/import/template/${kind}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `modelo-${kind}.xlsx`; a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="owner-import" className="space-y-5 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Importar dados</h1>
        <p className="text-sm text-zinc-400 mt-1">Traga clientes, carros e produtos de outro sistema por planilha (.xlsx ou .csv).</p>
      </div>
      <section className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
        <div className="text-[11px] font-mono-caps text-zinc-500">1. Tipo e arquivo</div>
        <div className="flex flex-wrap gap-2">
          {KINDS.map(([k, l]) => <button key={k} type="button" data-testid={`import-kind-${k}`} onClick={() => { setKind(k); reset(); }} className={`rounded-full border px-3 py-1.5 text-sm ${kind === k ? "border-green-600 text-green-300 bg-green-950/30" : "border-zinc-700 text-zinc-400"}`}>{l}</button>)}
          <Button data-testid="import-template" type="button" variant="outline" onClick={template} className="ml-auto h-9 border-zinc-700"><Download className="h-4 w-4 mr-1" /> Planilha modelo</Button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="import-file" type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); reset(); }} className="h-10 max-w-sm bg-zinc-900 border-zinc-800" />
          <Button data-testid="import-upload" onClick={upload} disabled={!file || busy} className="h-10 bg-green-brand hover:bg-green-brandDark text-white font-bold"><Upload className="h-4 w-4 mr-1" /> Enviar arquivo</Button>
        </div>
      </section>
      {parsed && fields && (
        <section data-testid="import-mapping" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-3">
          <div className="flex items-center gap-2 text-[11px] font-mono-caps text-zinc-500"><FileSpreadsheet className="h-4 w-4" /> 2. Colunas — {parsed.total} linha(s){parsed.truncated ? " (só as 5000 primeiras)" : ""}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fields[kind].map((f) => (
              <label key={f.key} className="flex items-center justify-between gap-2 text-sm text-zinc-300">
                <span>{f.label}{f.required && <span className="text-red-400"> *</span>}</span>
                <select data-testid={`import-map-${f.key}`} value={mapping[f.key] ?? ""} onChange={(e) => { setMapping((m) => ({ ...m, [f.key]: e.target.value === "" ? null : Number(e.target.value) })); setPreview(null); }}
                  className="h-9 w-44 rounded-md bg-zinc-900 border border-zinc-800 px-2 text-sm text-zinc-100">
                  <option value="">— não importar —</option>
                  {parsed.headers.map((h, i) => <option key={i} value={i}>{h || `Coluna ${i + 1}`}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-300">
            <span>Se já existir:</span>
            {[["skip", "Pular"], ["update", "Atualizar"]].map(([v, l]) => <label key={v} className="flex items-center gap-1"><input type="radio" data-testid={`import-mode-${v}`} checked={mode === v} onChange={() => { setMode(v); setPreview(null); }} className="accent-green-600" />{l}</label>)}
            <Button data-testid="import-preview-btn" onClick={() => run(true)} disabled={busy} variant="outline" className="ml-auto h-9 border-zinc-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "3. Ver prévia"}</Button>
          </div>
          <p className="text-[11px] text-zinc-500">Duplicados: clientes por CPF/CNPJ, telefone, e-mail ou placa; produtos pelo código (ou descrição quando não houver); serviços pelo código (ou nome quando não houver). Clientes importados ficam com origem "Importado".</p>
        </section>
      )}
      {preview && (
        <section className="space-y-2">
          <Report rep={preview} />
          <Button data-testid="import-run" onClick={() => run(false)} disabled={busy || preview.imported + preview.updated === 0} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `4. Importar ${preview.imported + preview.updated} linha(s)`}
          </Button>
        </section>
      )}
      {report && <Report rep={report} final />}
    </div>
  );
}
